import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import {
    Building2, Users, TrendingUp, Calendar, Bell, FileText,
    Plus, X, Edit2, Trash2, Check, Phone, Mail, Send,
    MapPin, DollarSign, Clock, Upload, User,
    ChevronRight, Undo2, XCircle, AlertCircle
} from 'lucide-react';
import { API_BASE, renderOptions, formatMoney, AssigneeMultiSelect, parseAssignees, logError, formatDateOnly, formatTimeOnly, describeSaveError } from './helpers';
import { getGraphCalendarToken, getGraphContactsToken, getGraphMailToken, getGraphMailSendToken } from '../graphTokenHelper';

// BUG-18: onDataChanged is App.js's fetchData, passed so an in-modal mutation
// (today: the lead stage stepper) can refresh the list behind the modal. Matches
// this tree's on<Event> prop convention; optional, so a caller that omits it
// simply keeps the previous behaviour.
function DetailModal({ type, item, onClose, onEdit, dropdownOptions, onNavigate, users, contacts, user, onDataChanged }) {
    // Publishing a document to the investor portal is investor-admin only
    // (enforced in routes/documents.js). Hiding the controls for everyone else
    // is the UX for that — no 403 ever reaches the user.
    const canPublishToInvestorDashboard = user?.role === 'admin' || user?.hasInvestorAdminAccess === true;
    const { instance: msalInstance, accounts: msalAccounts } = useMsal();
    const [properties, setProperties] = useState([]);
    const [notes, setNotes] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [activeDetailTab, setActiveDetailTab] = useState('overview');
    const [loadingDetail, setLoadingDetail] = useState(type !== 'contact');
    const [contactDetailLoading, setContactDetailLoading] = useState(type === 'contact');
    const [newNote, setNewNote] = useState('');
    const [activities, setActivities] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [newActivity, setNewActivity] = useState({ activityType: 'Call', description: '' });
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [uploadType, setUploadType] = useState('file');
    const [linkForm, setLinkForm] = useState({ fileName: '', externalUrl: '', documentType: 'Other', notes: '' });
    const [allProperties, setAllProperties] = useState([]);
    const [linkPropertyId, setLinkPropertyId] = useState('');
    const [owners, setOwners] = useState([]);
    const [allContacts, setAllContacts] = useState([]);
    const [addOwnerId, setAddOwnerId] = useState('');
    const [itemReminders, setItemReminders] = useState([]);
    const [newReminder, setNewReminder] = useState({ title: '', description: '', reminderType: 'Follow Up', dueDate: '', dueTime: '', priority: 'Medium', assignedToUserIds: [] });
    const [showReminderForm, setShowReminderForm] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(item.Status);
    const [showLostReason, setShowLostReason] = useState(false);
    const [lostReason, setLostReason] = useState('');
    const [outlookPushStatus, setOutlookPushStatus] = useState(null);
    const [pushingToOutlook, setPushingToOutlook] = useState(false);
    const [contactEmails, setContactEmails] = useState([]);
    const [emailsLoading, setEmailsLoading] = useState(false);
    const [emailTrackingEnabled, setEmailTrackingEnabled] = useState(true);
    const [emailsError, setEmailsError] = useState(null);
    const [togglingEmailTracking, setTogglingEmailTracking] = useState(false);
    const [emailMonths, setEmailMonths] = useState(24);
    const [emailsLoaded, setEmailsLoaded] = useState(false);

    // Close on Escape — same silent close as the overlay click.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);
    const [showComposeEmail, setShowComposeEmail] = useState(false);
    const [composeEmail, setComposeEmail] = useState({ to: '', subject: '', body: '', cc: '' });
    const [sendingEmail, setSendingEmail] = useState(false);
    const [composeError, setComposeError] = useState(null);

    // BUG-18 #27: ONE error slot for every quick action in this modal. Each of
    // these handlers owns its own axios call — none of them goes through App's
    // handleSave, so BUG-11's in-modal mechanism never covered them and a failed
    // note/reminder/link/upload/delete reported nothing but a console line.
    // Holds describeSaveError's { message, details, hint, gone } shape, or null.
    // Deliberately shared rather than duplicated per handler: there are 19 of
    // them, and only one action is ever in flight from a single click.
    // `composeError` stays separate — its slot sits inside the compose form,
    // which is better placed for that flow than the modal-level banner.
    const [actionError, setActionError] = useState(null);

    const itemId = type === 'contact' ? item.ContactId : type === 'property' ? item.PropertyId : type === 'contract' ? item.ContractId : item.LeadId;

    // Contact the "Send Email" compose targets. For a contact that's the
    // record itself; for a lead it's the lead's linked contact (Leads.ContactId).
    // Drives the shared /contacts/:id/send-email route so the send + activity
    // logging are identical to a contact-card send.
    const emailContactId = type === 'lead' ? item.ContactId : itemId;

    // Active users (with an email) offered as quick-pick CC recipients in the
    // compose form, alongside free-text entry. Sourced from the `users` prop
    // (GET /api/options/users/list) the modal already receives — no new fetch.
    const ccPickOptions = (users || []).filter(u => u.Email && u.Email.trim());

    // Contacts (with an email, excluding archived) offered as quick-pick CC
    // recipients alongside the teammate picker. Sourced from the `contacts` prop
    // the modal already receives from App state (loaded for the contacts/leads
    // tabs) — no new fetch. Archived contacts can still be CC'd by typing the
    // address into the free-text field deliberately.
    const ccContactOptions = (contacts || []).filter(c => c.Email && c.Email.trim() && !c.IsArchived);

    // Monotonic fetch sequence: every fetchDetailData call bumps it, and the
    // effect cleanup bumps it on re-key/unmount. In-flight responses compare
    // their captured value and drop their results when stale, so rapid
    // type/item switches can't paint the wrong record or set state after
    // unmount.
    const fetchSeqRef = useRef(0);

    useEffect(() => {
        setActiveDetailTab('overview');
        setCurrentStatus(item.Status);
        setContactEmails([]);
        setEmailsError(null);
        setEmailMonths(24);
        setEmailsLoaded(false);
        setShowComposeEmail(false);
        setComposeEmail({ to: '', subject: '', body: '', cc: '' });
        setSendingEmail(false);
        setComposeError(null);
        fetchDetailData();
        return () => { fetchSeqRef.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, itemId]);

    const fetchDetailData = async () => {
        const seq = ++fetchSeqRef.current;
        const stale = () => seq !== fetchSeqRef.current;
        if (type === 'contact') {
            setContactDetailLoading(true);
            try {
                const res = await axios.get(`${API_BASE}/contacts/${itemId}/detail`);
                if (stale()) return;
                const d = res.data;
                setProperties(d.properties);
                setNotes(d.notes);
                setContracts(d.contracts);
                setActivities(d.activities);
                setDocuments(d.documents);
                setAllProperties(d.allProperties);
                setItemReminders(d.reminders);
                setOutlookPushStatus(d.outlookStatus);
                const trackingEnabled = d.emailTrackingEnabled !== false;
                setEmailTrackingEnabled(trackingEnabled);
                // Auto-fetch emails for the overview activity timeline
                if (trackingEnabled && item.Email) {
                    fetchEmails(24);
                } else {
                    setEmailsLoaded(true);
                }
            } catch (err) {
                if (stale()) return;
                logError('Error fetching detail data:', err);
                setEmailsLoaded(true);
            } finally {
                if (!stale()) setContactDetailLoading(false);
            }
            return;
        }
        setLoadingDetail(true);
        try {
            if (type === 'property') {
                const [ownersRes, contactsRes, notesRes, documentsRes, remindersRes] = await Promise.all([
                    axios.get(`${API_BASE}/properties/${itemId}/owners`),
                    axios.get(`${API_BASE}/contacts`),
                    axios.get(`${API_BASE}/properties/${itemId}/notes`),
                    axios.get(`${API_BASE}/documents/property/${itemId}`),
                    axios.get(`${API_BASE}/reminders/by/Property/${itemId}`)
                ]);
                if (stale()) return;
                setOwners(ownersRes.data);
                setAllContacts(contactsRes.data);
                setNotes(notesRes.data);
                setDocuments(documentsRes.data);
                setItemReminders(remindersRes.data);
            } else if (type === 'contract') {
                const [propsRes, allPropsRes, notesRes, documentsRes, remindersRes] = await Promise.all([
                    axios.get(`${API_BASE}/contracts/${itemId}/properties`),
                    axios.get(`${API_BASE}/properties`),
                    axios.get(`${API_BASE}/contracts/${itemId}/notes`),
                    axios.get(`${API_BASE}/documents/contract/${itemId}`),
                    axios.get(`${API_BASE}/reminders/by/Contract/${itemId}`)
                ]);
                if (stale()) return;
                setProperties(propsRes.data);
                setAllProperties(allPropsRes.data);
                setNotes(notesRes.data);
                setDocuments(documentsRes.data);
                setItemReminders(remindersRes.data);
            } else {
                const [notesRes, activitiesRes, documentsRes, propsRes, allPropsRes, remindersRes] = await Promise.all([
                    axios.get(`${API_BASE}/leads/${itemId}/notes`),
                    axios.get(`${API_BASE}/activities/lead/${itemId}`),
                    axios.get(`${API_BASE}/documents/lead/${itemId}`),
                    axios.get(`${API_BASE}/leads/${itemId}/properties`),
                    axios.get(`${API_BASE}/properties`),
                    axios.get(`${API_BASE}/reminders/by/Lead/${itemId}`)
                ]);
                if (stale()) return;
                setNotes(notesRes.data);
                setActivities(activitiesRes.data);
                setDocuments(documentsRes.data);
                setProperties(propsRes.data);
                setAllProperties(allPropsRes.data);
                setItemReminders(remindersRes.data);
            }
        } catch (err) {
            if (stale()) return;
            logError('Error fetching detail data:', err);
        } finally {
            if (!stale()) setLoadingDetail(false);
        }
    };

    // Lead pipeline stages (in order)
    const LEAD_STAGES = ['New', 'Contacted', 'Qualified', 'Negotiating', 'Closed-Won'];
    const LEAD_STAGE_COLORS = {
        'New': '#2563eb',
        'Contacted': '#f59e0b',
        'Qualified': '#7c3aed',
        'Negotiating': '#e11d48',
        'Closed-Won': '#10b981',
        'Closed-Lost': '#78716c'
    };

    const currentStageIndex = LEAD_STAGES.indexOf(currentStatus);
    const isClosedStatus = currentStatus === 'Closed-Won' || currentStatus === 'Closed-Lost';
    const canAdvance = !isClosedStatus && currentStageIndex < LEAD_STAGES.length - 1;
    const canStepBack = !isClosedStatus && currentStageIndex > 0;
    const nextStage = canAdvance ? LEAD_STAGES[currentStageIndex + 1] : null;
    const prevStage = canStepBack ? LEAD_STAGES[currentStageIndex - 1] : null;

    const changeLeadStatus = async (newStatus, note) => {
        setActionError(null);
        try {
            await axios.put(`${API_BASE}/leads/${itemId}/status`, { status: newStatus, note: note || undefined });
            setCurrentStatus(newStatus);
            // Re-fetch activities to show new stage change entry
            const activitiesRes = await axios.get(`${API_BASE}/activities/lead/${itemId}`);
            setActivities(activitiesRes.data);
            // BUG-18: the stepper updated only this modal's local state, so the
            // status badge in the list BEHIND the modal kept showing the old
            // stage until something else happened to refetch. onDataChanged is
            // App.js's own fetchData — since BUG-10 removed the lastUpdate
            // counter, that is the tree's one refresh signal, and it leaves the
            // user's search box alone.
            if (onDataChanged) onDataChanged();
        } catch (err) {
            // BUG-18 #27: was alert() on 409 and console-only for everything else.
            // Both branches land in the shared banner now — describeSaveError
            // already prefers the server's own message, which is what the 409
            // alert was showing. The 409 is consequently logged too, which it
            // previously was not.
            logError('Error changing lead status:', err);
            setActionError(describeSaveError(err, 'lead', { hint: null }));
        }
    };

    const handleAdvanceStatus = () => {
        if (nextStage) changeLeadStatus(nextStage);
    };

    const handleStepBack = () => {
        if (prevStage) changeLeadStatus(prevStage);
    };

    const handleMarkLost = () => {
        if (!lostReason.trim()) return;
        changeLeadStatus('Closed-Lost', lostReason.trim());
        setShowLostReason(false);
        setLostReason('');
    };

    const addNote = async () => {
        if (!newNote.trim()) return;
        setActionError(null);
        try {
            const endpoint = type === 'property' ? 'properties' : `${type}s`;
            await axios.post(`${API_BASE}/${endpoint}/${itemId}/notes`, { noteText: newNote });
            setNewNote('');
            fetchDetailData();
        } catch (err) {
            logError('Error adding note:', err);
            setActionError(describeSaveError(err, 'note', { hint: null }));
        }
    };

    const createReminder = async () => {
        if (!newReminder.title.trim() || !newReminder.dueDate) return;
        setActionError(null);
        try {
            const relatedToType = type.charAt(0).toUpperCase() + type.slice(1);
            let calHeaders = {};
            try {
                const token = await getGraphCalendarToken(msalInstance, msalAccounts[0]);
                if (token) calHeaders = { 'X-Graph-Token': token };
            } catch { /* best-effort */ }
            await axios.post(`${API_BASE}/reminders`, {
                ...newReminder,
                relatedToType,
                relatedToId: itemId
            }, { headers: calHeaders });
            setNewReminder({ title: '', description: '', reminderType: 'Follow Up', dueDate: '', dueTime: '', priority: 'Medium', assignedToUserIds: [] });
            setShowReminderForm(false);
            fetchDetailData();
        } catch (err) {
            logError('Error creating reminder:', err);
            setActionError(describeSaveError(err, 'reminder', { hint: null }));
        }
    };

    const completeReminder = async (reminderId) => {
        setActionError(null);
        try {
            let calHeaders = {};
            try {
                const token = await getGraphCalendarToken(msalInstance, msalAccounts[0]);
                if (token) calHeaders = { 'X-Graph-Token': token };
            } catch { /* best-effort */ }
            await axios.put(`${API_BASE}/reminders/${reminderId}/complete`, {}, { headers: calHeaders });
            fetchDetailData();
        } catch (err) {
            logError('Error completing reminder:', err);
            setActionError(describeSaveError(err, 'reminder', { verb: 'complete', hint: null }));
        }
    };

    const pushToOutlook = async () => {
        setPushingToOutlook(true);
        try {
            const token = await getGraphContactsToken(msalInstance, msalAccounts[0]);
            if (!token) {
                alert('Could not acquire Outlook permission. Please allow the consent popup.');
                return;
            }
            const res = await axios.post(
                `${API_BASE}/contacts/${itemId}/push-to-outlook`,
                {},
                { headers: { 'X-Graph-Token': token } }
            );
            if (res.data.success) {
                setOutlookPushStatus({
                    pushed: true,
                    outlookContactId: res.data.outlookContactId,
                    lastPushedDate: new Date().toISOString()
                });
            } else {
                alert(`Failed to push to Outlook: ${res.data.error || 'Unknown error'}`);
            }
        } catch (err) {
            // BUG-18: the server used to answer a failed Outlook push with 200 +
            // {success:false}, so the specific reason arrived in the `else`
            // above. It is an honest 502/500 now, which lands here instead —
            // so the specific message is read off the error response to keep
            // the user as well informed as before.
            logError('Error pushing contact to Outlook:', err);
            alert(`Failed to push to Outlook: ${err.response?.data?.error || 'Please try again.'}`);
        } finally {
            setPushingToOutlook(false);
        }
    };

    const sendEmail = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(composeEmail.to)) {
            setComposeError('Please enter a valid email address.');
            return;
        }
        if (!composeEmail.subject.trim()) {
            setComposeError('Subject is required.');
            return;
        }
        if (!composeEmail.body.trim()) {
            setComposeError('Email body is required.');
            return;
        }

        // CC is optional, but every address (typed or picked) must be well-formed —
        // validate here so a bad entry is flagged in the form instead of failing the
        // Graph send. Reused as the payload's cc list below.
        const ccArray = composeEmail.cc
            ? composeEmail.cc.split(/[,;]/).map(s => s.trim()).filter(Boolean)
            : [];
        const invalidCc = ccArray.filter(addr => !emailRegex.test(addr));
        if (invalidCc.length > 0) {
            setComposeError(`Invalid CC address${invalidCc.length > 1 ? 'es' : ''}: ${invalidCc.join(', ')}`);
            return;
        }

        setSendingEmail(true);
        setComposeError(null);
        try {
            const token = await getGraphMailSendToken(msalInstance, msalAccounts[0]);
            if (!token) {
                setComposeError('Could not acquire mail send permission. Please allow the consent popup.');
                return;
            }

            const res = await axios.post(
                `${API_BASE}/contacts/${emailContactId}/send-email`,
                {
                    to: composeEmail.to,
                    subject: composeEmail.subject,
                    body: composeEmail.body,
                    cc: ccArray,
                    // Change A: when composing from a lead, tell the route to also
                    // log the send to that lead's activity feed (in addition to the
                    // contact row). Omitted for contact-card sends.
                    ...(type === 'lead' ? { leadId: item.LeadId } : {}),
                },
                { headers: { 'X-Graph-Token': token } }
            );

            if (res.data.success) {
                setShowComposeEmail(false);
                setComposeEmail({ to: '', subject: '', body: '', cc: '' });
                setComposeError(null);
                fetchDetailData();
            } else {
                setComposeError(res.data.error || 'Failed to send email.');
            }
        } catch (err) {
            logError('Error sending email:', err);
            setComposeError('Failed to send email. Please try again.');
        } finally {
            setSendingEmail(false);
        }
    };

    // Append a picked teammate's email to the free-text CC field, de-duping
    // (case-insensitive) against whatever is already typed there.
    const addCcRecipient = (email) => {
        const addr = (email || '').trim();
        if (!addr) return;
        setComposeEmail(prev => {
            const existing = prev.cc.split(/[,;]/).map(s => s.trim()).filter(Boolean);
            if (existing.some(e => e.toLowerCase() === addr.toLowerCase())) return prev;
            return { ...prev, cc: existing.concat(addr).join(', ') };
        });
        setComposeError(null);
    };

    const addActivity = async () => {
        // BUG-18: an activity with an empty description could be logged, leaving
        // a blank line on the timeline that says only "Call" and cannot be
        // edited. The server's SEC-29 gate validates the activity TYPE, not the
        // body, so nothing rejected it. Checked here, where the user can still
        // fix it, using the same alert() the other inline actions in this modal
        // use for validation.
        if (!newActivity.description || !newActivity.description.trim()) {
            alert('Please enter a description for this activity.');
            return;
        }

        setActionError(null);
        try {
            await axios.post(`${API_BASE}/activities/${type}/${itemId}`, newActivity);
            setNewActivity({ activityType: 'Call', description: '' });
            fetchDetailData();
        } catch (err) {
            logError('Error adding activity:', err);
            setActionError(describeSaveError(err, 'activity', { hint: null }));
        }
    };

    const addOwner = async () => {
        if (!addOwnerId) return;
        setActionError(null);
        try {
            await axios.post(`${API_BASE}/properties/${itemId}/owners`, { contactId: parseInt(addOwnerId) });
            setAddOwnerId('');
            fetchDetailData();
        } catch (err) {
            // BUG-18 #27: the hand-written 409 alert is gone; the server's own
            // "already an owner" message reaches the banner via describeSaveError.
            logError('Error adding owner:', err);
            setActionError(describeSaveError(err, 'owner link', { hint: null }));
        }
    };

    const removeOwner = async (contactId) => {
        setActionError(null);
        try {
            await axios.delete(`${API_BASE}/properties/${itemId}/owners/${contactId}`);
            fetchDetailData();
        } catch (err) {
            logError('Error removing owner:', err);
            setActionError(describeSaveError(err, 'owner link', { verb: 'remove', hint: null }));
        }
    };

    const linkContactProperty = async () => {
        if (!linkPropertyId) return;
        setActionError(null);
        try {
            await axios.post(`${API_BASE}/properties/${linkPropertyId}/owners`, { contactId: itemId });
            setLinkPropertyId('');
            fetchDetailData();
        } catch (err) {
            // BUG-18 #27: 409 alert removed — the server's message reaches the banner.
            logError('Error linking property:', err);
            setActionError(describeSaveError(err, 'property link', { hint: null }));
        }
    };

    const unlinkContactProperty = async (propertyId) => {
        setActionError(null);
        try {
            await axios.delete(`${API_BASE}/properties/${propertyId}/owners/${itemId}`);
            fetchDetailData();
        } catch (err) {
            logError('Error unlinking property:', err);
            setActionError(describeSaveError(err, 'property link', { verb: 'remove', hint: null }));
        }
    };

    const linkContractProperty = async () => {
        if (!linkPropertyId) return;
        setActionError(null);
        try {
            await axios.post(`${API_BASE}/contracts/${itemId}/properties`, { propertyId: parseInt(linkPropertyId) });
            setLinkPropertyId('');
            fetchDetailData();
        } catch (err) {
            // BUG-18 #27: 409 alert removed — the server's message reaches the banner.
            logError('Error linking property to contract:', err);
            setActionError(describeSaveError(err, 'property link', { hint: null }));
        }
    };

    const unlinkContractProperty = async (propertyId) => {
        setActionError(null);
        try {
            await axios.delete(`${API_BASE}/contracts/${itemId}/properties/${propertyId}`);
            fetchDetailData();
        } catch (err) {
            logError('Error unlinking property from contract:', err);
            setActionError(describeSaveError(err, 'property link', { verb: 'remove', hint: null }));
        }
    };

    const linkProperty = async () => {
        if (!linkPropertyId) return;
        setActionError(null);
        try {
            await axios.post(`${API_BASE}/leads/${itemId}/properties`, { propertyId: parseInt(linkPropertyId) });
            setLinkPropertyId('');
            fetchDetailData();
        } catch (err) {
            // BUG-18 #27: 409 alert removed — the server's message reaches the banner.
            logError('Error linking property:', err);
            setActionError(describeSaveError(err, 'property link', { hint: null }));
        }
    };

    const unlinkProperty = async (propertyId) => {
        setActionError(null);
        try {
            await axios.delete(`${API_BASE}/leads/${itemId}/properties/${propertyId}`);
            fetchDetailData();
        } catch (err) {
            logError('Error unlinking property:', err);
            setActionError(describeSaveError(err, 'property link', { verb: 'remove', hint: null }));
        }
    };

    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [isInvestorDashboard, setIsInvestorDashboard] = useState(false);
    const [isInvestorVisible, setIsInvestorVisible] = useState(false);

    const handleFilesSelected = (files) => {
        const allowed = ['.pdf','.doc','.docx','.xls','.xlsx','.ppt','.pptx','.txt','.csv','.jpg','.jpeg','.png','.gif'];
        const valid = Array.from(files).filter(f => {
            const ext = '.' + f.name.split('.').pop().toLowerCase();
            return allowed.includes(ext) && f.size <= 25 * 1024 * 1024;
        });
        setPendingFiles(prev => [...prev, ...valid]);
    };

    const removePendingFile = (index) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    const uploadPendingFiles = async () => {
        if (pendingFiles.length === 0) return;
        setUploading(true);
        setActionError(null);
        try {
            for (const file of pendingFiles) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('documentType', 'Other');
                if (isInvestorDashboard) {
                    formData.append('isInvestorDashboard', 'true');
                }
                if (isInvestorVisible) {
                    formData.append('isInvestorVisible', 'true');
                }
                await axios.post(`${API_BASE}/documents/${type}/${itemId}/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            setPendingFiles([]);
            setIsInvestorDashboard(false);
            setIsInvestorVisible(false);
            setShowUploadForm(false);
            fetchDetailData();
        } catch (err) {
            logError('Error uploading files:', err);
            setActionError(describeSaveError(err, 'document', { hint: null }));
        } finally {
            setUploading(false);
        }
    };

    const addLink = async () => {
        if (!linkForm.externalUrl.trim()) return;
        setActionError(null);
        try {
            await axios.post(`${API_BASE}/documents/${type}/${itemId}/link`, linkForm);
            setLinkForm({ fileName: '', externalUrl: '', documentType: 'Other', notes: '' });
            setShowUploadForm(false);
            fetchDetailData();
        } catch (err) {
            logError('Error adding link:', err);
            setActionError(describeSaveError(err, 'link', { hint: null }));
        }
    };

    const deleteDocument = async (documentId) => {
        if (window.confirm('Delete this document?')) {
            setActionError(null);
            try {
                await axios.delete(`${API_BASE}/documents/${type}/${itemId}/${documentId}`);
                fetchDetailData();
            } catch (err) {
                logError('Error deleting document:', err);
                // BUG-18 #27 follow-up: the verb mechanism cannot rescue the 404
                // on a delete — with verb 'delete' it reads "This document was
                // deleted by another user, so it can no longer be deleted", which
                // states the user's own goal as the obstacle. That single branch
                // gets a literal instead. It still REPORTS an error rather than
                // treating the 404 as success: whether a document that is already
                // gone should just close quietly is a behaviour question, and it
                // is deliberately not being answered here.
                setActionError(err.response?.status === 404
                    ? { message: 'This document was already deleted by another user.', details: [], hint: null, gone: true }
                    : describeSaveError(err, 'document', { verb: 'delete', hint: null }));
            }
        }
    };

    const toggleDashboardFlag = async (documentId, currentValue) => {
        setActionError(null);
        try {
            await axios.patch(`${API_BASE}/documents/${type}/${itemId}/${documentId}/dashboard`, {
                isInvestorDashboard: !currentValue
            });
            fetchDetailData();
        } catch (err) {
            logError('Error updating dashboard flag:', err);
            setActionError(describeSaveError(err, 'document', { verb: 'update', hint: null }));
        }
    };

    // Shares (or unshares) the document with the client portal's document list.
    // Separate endpoint from the dashboard flag above — the two are independent.
    const toggleInvestorVisibleFlag = async (documentId, currentValue) => {
        setActionError(null);
        try {
            await axios.patch(`${API_BASE}/documents/${type}/${itemId}/${documentId}/investor-visible`, {
                isInvestorVisible: !currentValue
            });
            fetchDetailData();
        } catch (err) {
            logError('Error updating investor visibility flag:', err);
            setActionError(describeSaveError(err, 'document', { verb: 'update', hint: null }));
        }
    };

    // Open an uploaded document in a new tab. The file lives in Azure Blob
    // Storage behind the authenticated /api/documents route, so it must be
    // fetched via axios (which carries the Bearer token) — a plain link /
    // new-tab navigation sends no Authorization header and can't load it.
    // Open the blank tab synchronously on the click, BEFORE the await, so the
    // browser keeps the user-gesture and doesn't block it as a popup; point it
    // at the blob URL once the download resolves.
    const openDocument = async (doc) => {
        const viewer = window.open('', '_blank');
        try {
            const res = await axios.get(
                `${API_BASE}/documents/${type}/${itemId}/${doc.DocumentId}/download`,
                { responseType: 'blob' }
            );
            const blob = res.data;
            const url = URL.createObjectURL(blob);
            // The server returns a real renderable content-type for inline-safe
            // files and forces application/octet-stream for everything else, so
            // mirror that decision: render those in the tab, download the rest
            // (closing the blank tab rather than leaving it empty). Also covers
            // the popup-blocked case, where `viewer` is null.
            const blobType = (blob.type || '').split(';')[0].trim().toLowerCase();
            const canInline = blobType && blobType !== 'application/octet-stream';
            if (canInline && viewer) {
                viewer.location = url;
            } else {
                if (viewer) viewer.close();
                const a = document.createElement('a');
                a.href = url;
                a.download = doc.FileName || 'document';
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            logError('Error opening document:', err);
            if (viewer) viewer.close();
            alert('Could not open the document. Please try again.');
        }
    };

    const fetchEmails = async (months) => {
        if (!item.Email || emailsLoading) return;
        // Read-only capture of the fetch sequence: if the modal re-keys or
        // unmounts (or a newer fetchDetailData starts) while this request is
        // in flight, drop the result instead of painting another contact's
        // emails.
        const seq = fetchSeqRef.current;
        const stale = () => seq !== fetchSeqRef.current;
        setEmailsLoading(true);
        setEmailsError(null);
        try {
            const token = await getGraphMailToken(msalInstance, msalAccounts[0]);
            if (stale()) return;
            if (!token) {
                setEmailsError('Could not acquire mail permission. Please allow the consent popup.');
                return;
            }
            const params = months !== undefined ? `?months=${months}` : '';
            const res = await axios.get(
                `${API_BASE}/contacts/${itemId}/emails${params}`,
                { headers: { 'X-Graph-Token': token } }
            );
            if (stale()) return;
            setContactEmails(res.data.emails || []);
            if (res.data.error) setEmailsError(res.data.error);
        } catch (err) {
            if (stale()) return;
            logError('Error fetching emails:', err);
            setEmailsError('Failed to load emails');
        } finally {
            if (!stale()) {
                setEmailsLoading(false);
                setEmailsLoaded(true);
            }
        }
    };

    const toggleEmailTracking = async () => {
        setTogglingEmailTracking(true);
        setActionError(null);
        try {
            const newValue = !emailTrackingEnabled;
            await axios.patch(`${API_BASE}/contacts/${itemId}/email-tracking`, { enabled: newValue });
            setEmailTrackingEnabled(newValue);
            if (newValue) {
                fetchEmails(emailMonths);
            } else {
                setContactEmails([]);
            }
        } catch (err) {
            logError('Error toggling email tracking:', err);
            setActionError(describeSaveError(err, 'contact', { verb: 'update', hint: null }));
        } finally {
            setTogglingEmailTracking(false);
        }
    };

    const detailTabs = type === 'contact'
        ? ['overview', 'properties', 'contracts', 'reminders', 'new activity', 'documents', 'notes']
        : type === 'property'
        ? ['overview', 'owners', 'reminders', 'documents', 'notes']
        : type === 'contract'
        ? ['overview', 'properties', 'reminders', 'documents', 'notes']
        : ['overview', 'properties', 'reminders', 'new activity', 'documents', 'notes'];

    const mergedTimeline = useMemo(() => {
        if (type !== 'contact' || contactEmails.length === 0) return activities;
        const merged = [
            ...activities.map(a => ({ ...a, _source: 'activity', _sortDate: new Date(a.CreatedDate).getTime() })),
            ...contactEmails.map(e => ({ ...e, _source: 'email', _sortDate: new Date(e.receivedDateTime).getTime() })),
        ];
        merged.sort((a, b) => b._sortDate - a._sortDate);
        return merged;
    }, [type, activities, contactEmails]);

    const contactLoading = type === 'contact' && contactDetailLoading;
    const inlineLoading = (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div className="crm-loading__spinner" style={{ width: '20px', height: '20px', margin: '0 auto', borderWidth: '2px' }} />
        </div>
    );

    return (
        <div className="crm-modal__overlay" onClick={onClose}>
            <div className="crm-detail-modal crm-detail-modal--wide" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="crm-detail-modal__header">
                    <div className="crm-detail-modal__header-content">
                        <div className="crm-contact-card__avatar">
                            {type === 'property'
                              ? (item.PropertyName || 'P')[0]
                              : type === 'contract'
                              ? (item.ContractNumber || 'C')[0]
                              : <>{(type === 'lead' ? (item.ContactFirstName || item.FirstName) : item.FirstName)?.[0]}{(type === 'lead' ? (item.ContactLastName || item.LastName) : item.LastName)?.[0]}</>
                            }
                        </div>
                        <div>
                            <h2 className="crm-detail-modal__name">
                                {type === 'property'
                                  ? item.PropertyName
                                  : type === 'contract'
                                  ? item.ContractNumber
                                  : <>{type === 'lead' ? (item.ContactFirstName || item.FirstName) : item.FirstName} {type === 'lead' ? (item.ContactLastName || item.LastName) : item.LastName}</>
                                }
                            </h2>
                            {type === 'property' ? (
                              item.PropertyAddress && (
                                <p className="crm-detail-modal__company">
                                  {item.PropertyAddress}{item.City && `, ${item.City}`}{item.State && `, ${item.State}`}
                                </p>
                              )
                            ) : type === 'contract' ? (
                              item.ContactName && (
                                <p className="crm-detail-modal__company">{item.ContactName}{item.ContactCompany ? ` — ${item.ContactCompany}` : ''}</p>
                              )
                            ) : (
                              (type === 'lead' ? (item.ContactCompany || item.CompanyName) : item.CompanyName) && (
                                <p className="crm-detail-modal__company">{type === 'lead' ? (item.ContactCompany || item.CompanyName) : item.CompanyName}</p>
                              )
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                <span className={`crm-badge ${type === 'contact' ? 'crm-badge--taupe' : type === 'property' ? 'crm-badge--blue' : type === 'contract' ? 'crm-badge--blue' : 'crm-badge--blue'}`}>
                                    {type === 'contact' ? item.ContactType : type === 'property' ? (item.PropertyType || 'Property') : type === 'contract' ? item.ContractType : type === 'lead' ? item.Source || 'Lead' : item.Status}
                                </span>
                                {type === 'contract' && item.Status && (
                                  <span className={`crm-badge ${item.Status === 'Active' ? 'crm-badge--green' : item.Status === 'Signed' ? 'crm-badge--blue' : item.Status === 'Draft' ? 'crm-badge--yellow' : 'crm-badge--blue'}`}>
                                    {item.Status}
                                  </span>
                                )}
                                {type === 'property' && item.Status && (
                                  <span className={`crm-badge ${item.Status === 'Active' ? 'crm-badge--green' : item.Status === 'Vacant' ? 'crm-badge--yellow' : 'crm-badge--blue'}`}>
                                    {item.Status}
                                  </span>
                                )}
                                <button onClick={() => onEdit(item)} className="crm-btn crm-btn--secondary" style={{ padding: '2px 10px', fontSize: '12px', lineHeight: '1.4' }}>
                                    <Edit2 size={13} style={{ marginRight: '4px' }} /> Edit
                                </button>
                                <button onClick={() => setShowReminderForm(prev => !prev)} className="crm-btn crm-btn--secondary" style={{ padding: '2px 10px', fontSize: '12px', lineHeight: '1.4' }}>
                                    <Bell size={13} style={{ marginRight: '4px' }} /> Reminder
                                </button>
                                {type === 'contact' && (
                                    <button
                                        onClick={pushToOutlook}
                                        disabled={pushingToOutlook}
                                        className="crm-btn crm-btn--secondary"
                                        style={{ padding: '2px 10px', fontSize: '12px', lineHeight: '1.4' }}
                                        data-tooltip={outlookPushStatus?.pushed
                                            ? `Last pushed ${new Date(outlookPushStatus.lastPushedDate).toLocaleDateString()}`
                                            : 'Create this contact in your Outlook'}
                                    >
                                        <Mail size={13} style={{ marginRight: '4px' }} />
                                        {pushingToOutlook
                                            ? 'Pushing...'
                                            : outlookPushStatus?.pushed
                                                ? 'Update in Outlook'
                                                : 'Push to Outlook'}
                                    </button>
                                )}
                                {type === 'contact' && item.Email && (
                                    <button
                                        onClick={() => {
                                            setShowComposeEmail(prev => !prev);
                                            if (!showComposeEmail) {
                                                setComposeEmail(prev => ({ ...prev, to: item.Email }));
                                                setComposeError(null);
                                            }
                                        }}
                                        className="crm-btn crm-btn--secondary"
                                        style={{ padding: '2px 10px', fontSize: '12px', lineHeight: '1.4' }}
                                    >
                                        <Send size={13} style={{ marginRight: '4px' }} /> Send Email
                                    </button>
                                )}
                                {type === 'lead' && item.ContactId && item.ContactEmail && (
                                    <button
                                        onClick={() => {
                                            setShowComposeEmail(prev => !prev);
                                            if (!showComposeEmail) {
                                                setComposeEmail(prev => ({ ...prev, to: item.ContactEmail }));
                                                setComposeError(null);
                                            }
                                        }}
                                        className="crm-btn crm-btn--secondary"
                                        style={{ padding: '2px 10px', fontSize: '12px', lineHeight: '1.4' }}
                                        data-tooltip={`Email ${item.ContactFirstName || ''} ${item.ContactLastName || ''}`.trim()}
                                    >
                                        <Send size={13} style={{ marginRight: '4px' }} /> Send Email
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="crm-btn crm-btn--secondary crm-btn--icon" data-tooltip="Close">
                        <X size={20} />
                    </button>
                </div>

                {/* Lead Pipeline Stepper */}
                {type === 'lead' && (
                    <div className="crm-pipeline">
                        {/* Stage indicators */}
                        <div className="crm-pipeline__track">
                            {LEAD_STAGES.map((stage, idx) => {
                                const stageColor = LEAD_STAGE_COLORS[stage];
                                const isCurrent = stage === currentStatus;
                                const isPast = currentStageIndex >= 0 && idx < currentStageIndex;
                                const isLost = currentStatus === 'Closed-Lost';

                                return (
                                    <React.Fragment key={stage}>
                                        {idx > 0 && (
                                            <div
                                                className={`crm-pipeline__connector ${isPast ? 'crm-pipeline__connector--past' : ''} ${isCurrent ? 'crm-pipeline__connector--current' : ''}`}
                                                style={{ background: isPast || isCurrent ? stageColor : undefined }}
                                            />
                                        )}
                                        <div className="crm-pipeline__stage">
                                            <div
                                                className={`crm-pipeline__dot ${isCurrent ? 'crm-pipeline__dot--current' : ''} ${isPast ? 'crm-pipeline__dot--past' : ''} ${isLost && !isCurrent ? 'crm-pipeline__dot--dimmed' : ''}`}
                                                style={{
                                                    background: isCurrent || isPast ? stageColor : undefined,
                                                    border: isCurrent ? `2px solid ${stageColor}` : undefined,
                                                    boxShadow: isCurrent ? `0 0 0 3px ${stageColor}25` : undefined
                                                }}
                                            />
                                            <span
                                                className={`crm-pipeline__label ${isCurrent ? 'crm-pipeline__label--current' : ''} ${isPast ? 'crm-pipeline__label--past' : ''} ${isLost && !isCurrent ? 'crm-pipeline__label--dimmed' : ''}`}
                                                style={{ color: isCurrent ? stageColor : undefined }}
                                            >
                                                {stage}
                                            </span>
                                        </div>
                                    </React.Fragment>
                                );
                            })}

                            {/* Closed-Lost indicator (shown only when lost) */}
                            {currentStatus === 'Closed-Lost' && (
                                <>
                                    <div className="crm-pipeline__connector crm-pipeline__connector--lost" style={{ background: LEAD_STAGE_COLORS['Closed-Lost'] }} />
                                    <div className="crm-pipeline__stage">
                                        <div
                                            className="crm-pipeline__dot crm-pipeline__dot--current"
                                            style={{
                                                background: LEAD_STAGE_COLORS['Closed-Lost'],
                                                border: `2px solid ${LEAD_STAGE_COLORS['Closed-Lost']}`,
                                                boxShadow: `0 0 0 3px ${LEAD_STAGE_COLORS['Closed-Lost']}25`
                                            }}
                                        />
                                        <span className="crm-pipeline__label crm-pipeline__label--current" style={{ color: LEAD_STAGE_COLORS['Closed-Lost'] }}>
                                            Closed-Lost
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Action buttons */}
                        {!isClosedStatus && (
                            <div className="crm-pipeline__actions">
                                {canStepBack && (
                                    <button
                                        onClick={handleStepBack}
                                        className="crm-btn crm-btn--secondary crm-pipeline__btn"
                                        data-tooltip={`Back to ${prevStage}`}
                                    >
                                        <Undo2 size={12} />
                                        {prevStage}
                                    </button>
                                )}
                                {canAdvance && (
                                    <button
                                        onClick={handleAdvanceStatus}
                                        className="crm-btn crm-btn--primary crm-pipeline__btn"
                                    >
                                        Advance to {nextStage}
                                        <ChevronRight size={13} />
                                    </button>
                                )}
                                <div className="crm-pipeline__spacer" />
                                {!showLostReason ? (
                                    <button
                                        onClick={() => setShowLostReason(true)}
                                        className="crm-btn crm-btn--secondary crm-pipeline__btn crm-pipeline__btn--lost"
                                    >
                                        <XCircle size={12} />
                                        Mark Lost
                                    </button>
                                ) : (
                                    <div className="crm-pipeline__lost-form">
                                        <input
                                            type="text"
                                            placeholder="Reason for losing this lead *"
                                            value={lostReason}
                                            onChange={(e) => setLostReason(e.target.value)}
                                            className="crm-form__input crm-pipeline__lost-input"
                                            autoFocus
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleMarkLost(); if (e.key === 'Escape') { setShowLostReason(false); setLostReason(''); } }}
                                        />
                                        <button
                                            onClick={handleMarkLost}
                                            className="crm-btn crm-btn--danger crm-pipeline__btn"
                                            disabled={!lostReason.trim()}
                                        >
                                            Confirm
                                        </button>
                                        <button
                                            onClick={() => { setShowLostReason(false); setLostReason(''); }}
                                            className="crm-btn crm-btn--secondary crm-pipeline__btn"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Closed status message */}
                        {isClosedStatus && (
                            <div className="crm-pipeline__status-msg" style={{ color: LEAD_STAGE_COLORS[currentStatus] }}>
                                {currentStatus === 'Closed-Won' ? '✓ This lead has been converted' : '✕ This lead was marked as lost'}
                            </div>
                        )}
                    </div>
                )}

                {/* Inline Add Reminder Form */}
                {showReminderForm && (
                    <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--input-border)', background: 'var(--background)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                                <Bell size={14} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                                New Reminder
                            </h4>
                            <button onClick={() => setShowReminderForm(false)} className="crm-btn crm-btn--secondary crm-btn--icon" style={{ padding: '2px' }}>
                                <X size={14} />
                            </button>
                        </div>
                        <div className="crm-form__row">
                            <div className="crm-form__group crm-form__col">
                                <input
                                    type="text"
                                    placeholder="Reminder title *"
                                    value={newReminder.title}
                                    onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                                    className="crm-form__input"
                                />
                            </div>
                            <div className="crm-form__group crm-form__col" style={{ flex: '0 0 140px' }}>
                                <select
                                    value={newReminder.reminderType}
                                    onChange={(e) => setNewReminder(prev => ({ ...prev, reminderType: e.target.value }))}
                                    className="crm-form__select"
                                >
                                    {renderOptions(dropdownOptions.ReminderType)}
                                </select>
                            </div>
                        </div>
                        <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">Due Date *</label>
                                <input
                                    type="date"
                                    value={newReminder.dueDate}
                                    onChange={(e) => setNewReminder(prev => ({ ...prev, dueDate: e.target.value }))}
                                    className="crm-form__input"
                                    placeholder="Due date *"
                                />
                            </div>
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">Due Time</label>
                                <input
                                    type="time"
                                    value={newReminder.dueTime}
                                    onChange={(e) => setNewReminder(prev => ({ ...prev, dueTime: e.target.value }))}
                                    className="crm-form__input"
                                />
                            </div>
                            <div className="crm-form__group crm-form__col" style={{ flex: '0 0 120px' }}>
                                <select
                                    value={newReminder.priority}
                                    onChange={(e) => setNewReminder(prev => ({ ...prev, priority: e.target.value }))}
                                    className="crm-form__select"
                                >
                                    {renderOptions(dropdownOptions.ReminderPriority)}
                                </select>
                            </div>
                        </div>
                        <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">Assigned To</label>
                                <AssigneeMultiSelect
                                    users={users}
                                    value={newReminder.assignedToUserIds}
                                    onChange={(ids) => setNewReminder(prev => ({ ...prev, assignedToUserIds: ids }))}
                                />
                            </div>
                        </div>
                        <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                            <div className="crm-form__group crm-form__col">
                                <input
                                    type="text"
                                    placeholder="Description (optional)"
                                    value={newReminder.description}
                                    onChange={(e) => setNewReminder(prev => ({ ...prev, description: e.target.value }))}
                                    className="crm-form__input"
                                />
                            </div>
                            <button
                                onClick={createReminder}
                                className="crm-btn crm-btn--primary"
                                disabled={!newReminder.title.trim() || !newReminder.dueDate}
                                style={{ flexShrink: 0, alignSelf: 'flex-end' }}
                            >
                                <Plus size={16} /> Save
                            </button>
                        </div>
                    </div>
                )}

                {/* Compose Email Form */}
                {showComposeEmail && (
                    <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--input-border)', background: 'var(--background)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                                <Send size={14} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                                Compose Email
                            </h4>
                            <button onClick={() => { setShowComposeEmail(false); setComposeError(null); }} className="crm-btn crm-btn--secondary crm-btn--icon" style={{ padding: '2px' }}>
                                <X size={14} />
                            </button>
                        </div>
                        <div className="crm-form__row">
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">To *</label>
                                <input
                                    type="email"
                                    value={composeEmail.to}
                                    onChange={(e) => setComposeEmail(prev => ({ ...prev, to: e.target.value }))}
                                    className="crm-form__input"
                                    placeholder="recipient@example.com"
                                />
                            </div>
                        </div>
                        <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">CC</label>
                                <input
                                    type="text"
                                    value={composeEmail.cc}
                                    onChange={(e) => setComposeEmail(prev => ({ ...prev, cc: e.target.value }))}
                                    className="crm-form__input"
                                    placeholder="Comma-separated email addresses"
                                />
                                {ccPickOptions.length > 0 && (
                                    <select
                                        className="crm-form__input"
                                        value=""
                                        onChange={(e) => addCcRecipient(e.target.value)}
                                        style={{ marginTop: '0.4rem' }}
                                    >
                                        <option value="">+ Add teammate to CC…</option>
                                        {ccPickOptions.map(u => (
                                            <option key={u.UserId} value={u.Email}>
                                                {u.FirstName} {u.LastName} ({u.Email})
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {ccContactOptions.length > 0 && (
                                    <select
                                        className="crm-form__input"
                                        value=""
                                        onChange={(e) => addCcRecipient(e.target.value)}
                                        style={{ marginTop: '0.4rem' }}
                                    >
                                        <option value="">+ Add contact to CC…</option>
                                        {ccContactOptions.map(c => (
                                            <option key={c.ContactId} value={c.Email}>
                                                {c.FirstName} {c.LastName} ({c.Email})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">Subject *</label>
                                <input
                                    type="text"
                                    value={composeEmail.subject}
                                    onChange={(e) => setComposeEmail(prev => ({ ...prev, subject: e.target.value }))}
                                    className="crm-form__input"
                                    placeholder="Email subject"
                                />
                            </div>
                        </div>
                        <div className="crm-form__row" style={{ marginTop: '0.5rem' }}>
                            <div className="crm-form__group crm-form__col">
                                <label className="crm-form__label">Body *</label>
                                <textarea
                                    value={composeEmail.body}
                                    onChange={(e) => setComposeEmail(prev => ({ ...prev, body: e.target.value }))}
                                    className="crm-form__input"
                                    rows={6}
                                    placeholder="Email body"
                                />
                            </div>
                        </div>
                        {/* Future: template dropdown */}
                        {composeError && (
                            <div className="crm-error" style={{ marginTop: '0.5rem' }}>{composeError}</div>
                        )}
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => { setShowComposeEmail(false); setComposeError(null); }}
                                className="crm-btn crm-btn--secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={sendEmail}
                                className="crm-btn crm-btn--primary"
                                disabled={sendingEmail || !composeEmail.to.trim() || !composeEmail.subject.trim() || !composeEmail.body.trim()}
                            >
                                <Send size={14} style={{ marginRight: '4px' }} />
                                {sendingEmail ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="crm-detail-modal__tabs">
                    {detailTabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => { setActiveDetailTab(tab); setActionError(null); }}
                            className={`crm-detail-modal__tab ${activeDetailTab === tab ? 'crm-detail-modal__tab--active' : ''}`}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {/* BUG-18 #27: the shared quick-action error slot. Deliberately a
                    SIBLING of the tab strip and the body, not a child of either:
                    .crm-detail-modal is a flex column, so the banner pins here —
                    below the tabs, above the scroll area — and stays visible in
                    every tab. Inside __body or __left-panel it would scroll out of
                    view (both are overflow-y:auto), which is the invisibility this
                    fixes; and __body--split is a two-column grid, so a child there
                    would become a third column and break the lead/contact layout.
                    Dismissable because this modal outlives many actions. */}
                {actionError && (
                    <div className="crm-error" style={{ margin: '1rem 1.5rem 0', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <AlertCircle size={18} className="crm-error__icon" />
                        <div style={{ flex: 1 }}>
                            {actionError.message}
                            {actionError.details.length > 0 && (
                                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
                                    {actionError.details.map((d, i) => <li key={i}>{d}</li>)}
                                </ul>
                            )}
                            {actionError.hint && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.813rem' }}>{actionError.hint}</div>
                            )}
                        </div>
                        <button
                            onClick={() => setActionError(null)}
                            className="crm-btn crm-btn--secondary crm-btn--icon"
                            data-tooltip="Dismiss"
                            aria-label="Dismiss error"
                        >
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Body */}
                <div className={`crm-detail-modal__body ${(type === 'lead' || type === 'contact') ? 'crm-detail-modal__body--split' : ''}`}>
                    {(loadingDetail && type !== 'contact') ? (
                        <div className="crm-loading">
                            <div className="crm-loading__container">
                                <div className="crm-loading__spinner" />
                                <p className="crm-loading__text">Loading...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* LEFT PANEL â€" Tab Content */}
                            <div className={(type === 'lead' || type === 'contact') ? 'crm-detail-modal__left-panel' : ''}>

                            {/* Overview Tab */}
                            {activeDetailTab === 'overview' && (
                                <div className="crm-detail-modal__overview">
                                    <div className="crm-detail-modal__info-grid">
                                        {item.Email && (
                                            <div className="crm-detail-modal__info-item">
                                                <Mail size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Email</div>
                                                    <div className="crm-detail-modal__info-value">{item.Email}</div>
                                                </div>
                                            </div>
                                        )}
                                        {item.Phone && (
                                            <div className="crm-detail-modal__info-item">
                                                <Phone size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Phone</div>
                                                    <div className="crm-detail-modal__info-value">{item.Phone}</div>
                                                </div>
                                            </div>
                                        )}
                                        {item.MobilePhone && (
                                            <div className="crm-detail-modal__info-item">
                                                <Phone size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Mobile</div>
                                                    <div className="crm-detail-modal__info-value">{item.MobilePhone}</div>
                                                </div>
                                            </div>
                                        )}
                                        {item.Address && (
                                            <div className="crm-detail-modal__info-item">
                                                <MapPin size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Address</div>
                                                    <div className="crm-detail-modal__info-value">
                                                        {item.Address}
                                                        {item.City && `, ${item.City}`}
                                                        {item.State && `, ${item.State}`}
                                                        {item.ZipCode && ` ${item.ZipCode}`}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {type === 'contact' && item.PreferredContactMethod && (
                                            <div className="crm-detail-modal__info-item">
                                                <Bell size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Preferred Contact</div>
                                                    <div className="crm-detail-modal__info-value">{item.PreferredContactMethod}</div>
                                                </div>
                                            </div>
                                        )}
                                        {type === 'contact' && item.LastContactDate && (
                                            <div className="crm-detail-modal__info-item">
                                                <Clock size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Last Contact</div>
                                                    <div className="crm-detail-modal__info-value">
                                                        {formatDateOnly(item.LastContactDate)}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {type === 'lead' && item.Source && (
                                            <div className="crm-detail-modal__info-item">
                                                <TrendingUp size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Source</div>
                                                    <div className="crm-detail-modal__info-value">{item.Source}</div>
                                                </div>
                                            </div>
                                        )}
                                        {type === 'lead' && item.EstimatedValue && (
                                            <div className="crm-detail-modal__info-item">
                                                <DollarSign size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Estimated Value</div>
                                                    <div className="crm-detail-modal__info-value">
                                                        {formatMoney(item.EstimatedValue)}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {type === 'lead' && item.NextFollowUpDate && (
                                            <div className="crm-detail-modal__info-item">
                                                <Calendar size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Follow-Up Date</div>
                                                    <div className="crm-detail-modal__info-value">
                                                        {formatDateOnly(item.NextFollowUpDate)}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {type === 'lead' && item.PropertyName && (
                                            <div className="crm-detail-modal__info-item">
                                                <Building2 size={16} className="crm-detail-modal__info-icon" />
                                                <div>
                                                    <div className="crm-detail-modal__info-label">Linked Property</div>
                                                    <div className="crm-detail-modal__info-value">
                                                        {item.PropertyName}
                                                        {item.LinkedPropertyAddress && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}> — {item.LinkedPropertyAddress}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {(item.Notes || item.PropertyInterest) && (
                                        <div style={{ marginTop: '1.5rem' }}>
                                            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                                                {type === 'contact' ? 'Notes' : 'Property Interest'}
                                            </h3>
                                            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                                                {type === 'contact' ? item.Notes : item.PropertyInterest}
                                            </p>
                                        </div>
                                    )}

                                    {/* Property Overview Fields */}
                                    {type === 'property' && (
                                      <>
                                        <div className="crm-detail-modal__info-grid">
                                            {item.OwnerName && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Users size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Owner</div>
                                                        <div className="crm-detail-modal__info-value">
                                                            {item.OwnerName}{item.OwnerCompany ? ` — ${item.OwnerCompany}` : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.YardiCode && (
                                                <div className="crm-detail-modal__info-item">
                                                    <FileText size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Yardi Code</div>
                                                        <div className="crm-detail-modal__info-value" style={{ fontFamily: 'monospace', fontWeight: '600' }}>{item.YardiCode}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.Units && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Building2 size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Units</div>
                                                        <div className="crm-detail-modal__info-value">{item.Units}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.MarketValue && (
                                                <div className="crm-detail-modal__info-item">
                                                    <DollarSign size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Market Value</div>
                                                        <div className="crm-detail-modal__info-value">${Number(item.MarketValue).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.YearBuilt && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Calendar size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Year Built</div>
                                                        <div className="crm-detail-modal__info-value">{item.YearBuilt}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.AcquisitionDate && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Calendar size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Acquired</div>
                                                        <div className="crm-detail-modal__info-value">{formatDateOnly(item.AcquisitionDate)}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.RenovationDate && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Clock size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Last Renovation</div>
                                                        <div className="crm-detail-modal__info-value">{formatDateOnly(item.RenovationDate)}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.MonthlyManagementFee && (
                                                <div className="crm-detail-modal__info-item">
                                                    <DollarSign size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Monthly Mgmt Fee</div>
                                                        <div className="crm-detail-modal__info-value">${Number(item.MonthlyManagementFee).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.MonthlyRevenue && (
                                                <div className="crm-detail-modal__info-item">
                                                    <DollarSign size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Monthly Revenue</div>
                                                        <div className="crm-detail-modal__info-value" style={{ color: 'var(--success)', fontWeight: '600' }}>
                                                            ${Number(item.MonthlyRevenue).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.OwnerEmail && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Mail size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Owner Email</div>
                                                        <div className="crm-detail-modal__info-value">{item.OwnerEmail}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.OwnerPhone && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Phone size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Owner Phone</div>
                                                        <div className="crm-detail-modal__info-value">{item.OwnerPhone}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {item.Notes && (
                                            <div style={{ marginTop: '1.5rem' }}>
                                                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Notes</h3>
                                                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                                                    {item.Notes}
                                                </p>
                                            </div>
                                        )}
                                      </>
                                    )}

                                    {/* Contract Overview Fields */}
                                    {type === 'contract' && (
                                      <>
                                        <div className="crm-detail-modal__info-grid">
                                            {item.ContactName && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Users size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Contact (Owner)</div>
                                                        <div className="crm-detail-modal__info-value">{item.ContactName}{item.ContactCompany ? ` — ${item.ContactCompany}` : ''}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.LeadId && (
                                                <div className="crm-detail-modal__info-item">
                                                    <TrendingUp size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Converted from Lead</div>
                                                        <div className="crm-detail-modal__info-value">{item.LeadContactName || `Lead #${item.LeadId}`}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.Value && (
                                                <div className="crm-detail-modal__info-item">
                                                    <DollarSign size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Contract Value</div>
                                                        <div className="crm-detail-modal__info-value">${Number(item.Value).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.StartDate && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Calendar size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Start Date</div>
                                                        <div className="crm-detail-modal__info-value">{formatDateOnly(item.StartDate)}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.EndDate && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Calendar size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">End Date</div>
                                                        <div className="crm-detail-modal__info-value">{formatDateOnly(item.EndDate)}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.SignedDate && (
                                                <div className="crm-detail-modal__info-item">
                                                    <Check size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Signed Date</div>
                                                        <div className="crm-detail-modal__info-value">{formatDateOnly(item.SignedDate)}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {item.PropertyAddress && (
                                                <div className="crm-detail-modal__info-item">
                                                    <MapPin size={16} className="crm-detail-modal__info-icon" />
                                                    <div>
                                                        <div className="crm-detail-modal__info-label">Property Address</div>
                                                        <div className="crm-detail-modal__info-value">{item.PropertyAddress}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {item.Terms && (
                                            <div style={{ marginTop: '1.5rem' }}>
                                                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Terms</h3>
                                                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{item.Terms}</p>
                                            </div>
                                        )}
                                        {item.Notes && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Notes</h3>
                                                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{item.Notes}</p>
                                            </div>
                                        )}
                                      </>
                                    )}
                                </div>
                            )}

                            {/* Owners Tab (Property only) */}
                            {type === 'property' && activeDetailTab === 'owners' && (
                                <div>
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                                        <select
                                            value={addOwnerId}
                                            onChange={(e) => setAddOwnerId(e.target.value)}
                                            className="crm-form__select"
                                            style={{ flex: 1 }}
                                        >
                                            <option value="">Add an owner...</option>
                                            {allContacts
                                                .filter(c => !owners.some(o => o.ContactId === c.ContactId))
                                                .map(c => (
                                                    <option key={c.ContactId} value={c.ContactId}>
                                                        {c.FirstName} {c.LastName}{c.CompanyName ? ` — ${c.CompanyName}` : ''} ({c.ContactType})
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        <button
                                            onClick={addOwner}
                                            disabled={!addOwnerId}
                                            className="crm-btn crm-btn--primary"
                                        >
                                            <Plus size={16} /> Add
                                        </button>
                                    </div>

                                    {owners.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No owners linked</p>
                                    ) : (
                                        <div className="crm-detail-modal__list">
                                            {owners.map(owner => (
                                                <div key={owner.ContactId} className="crm-detail-modal__list-item" onClick={() => onNavigate && onNavigate('contact', owner)} style={{ cursor: onNavigate ? 'pointer' : undefined }}>
                                                    <div className="crm-detail-modal__list-item-header">
                                                        <h4 className="crm-detail-modal__list-item-title">
                                                            {owner.FirstName} {owner.LastName}
                                                        </h4>
                                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                            {owner.ContactType && (
                                                                <span className="crm-badge crm-badge--taupe">{owner.ContactType}</span>
                                                            )}
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); removeOwner(owner.ContactId); }}
                                                                className="crm-btn crm-btn--secondary crm-btn--icon"
                                                                style={{ padding: '2px 6px' }}
                                                                data-tooltip="Remove owner"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {owner.CompanyName && (
                                                        <p style={{ fontSize: '0.813rem', color: 'var(--text-muted)', margin: '0.25rem 0' }}>
                                                            {owner.CompanyName}
                                                        </p>
                                                    )}
                                                    <div className="crm-detail-modal__list-item-meta">
                                                        {owner.Email && <span>{owner.Email}</span>}
                                                        {owner.Phone && <span>{owner.Phone}</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Properties Tab (Contact + Lead + Contract) */}
                            {(type === 'contact' || type === 'lead' || type === 'contract') && activeDetailTab === 'properties' && (
                                <div>
                                    {contactLoading ? inlineLoading : (<>
                                    {/* Link property dropdown */}
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                                        <select
                                            value={linkPropertyId}
                                            onChange={(e) => setLinkPropertyId(e.target.value)}
                                            className="crm-form__select"
                                            style={{ flex: 1, minWidth: 0 }}
                                        >
                                            <option value="">Link a property...</option>
                                            {allProperties
                                                .filter(p => !properties.some(lp => lp.PropertyId === p.PropertyId))
                                                .map(p => (
                                                    <option key={p.PropertyId} value={p.PropertyId}>
                                                        {p.PropertyName}{p.YardiCode ? ` [${p.YardiCode}]` : ''} — {p.PropertyAddress || 'No address'}{p.City ? `, ${p.City}` : ''}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        <button
                                            onClick={type === 'contact' ? linkContactProperty : type === 'contract' ? linkContractProperty : linkProperty}
                                            disabled={!linkPropertyId}
                                            className="crm-btn crm-btn--primary"
                                            style={{ flexShrink: 0 }}
                                        >
                                            <Plus size={16} /> Link
                                        </button>
                                    </div>

                                    {properties.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No properties linked</p>
                                    ) : (
                                        <div className="crm-detail-modal__list">
                                            {properties.map(prop => (
                                                <div key={prop.PropertyId} className="crm-detail-modal__list-item" onClick={() => onNavigate && onNavigate('property', prop)} style={{ cursor: onNavigate ? 'pointer' : undefined }}>
                                                    <div className="crm-detail-modal__list-item-header">
                                                        <h4 className="crm-detail-modal__list-item-title">
                                                            {prop.PropertyName}
                                                            {prop.YardiCode && <span style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'var(--text-muted)', marginLeft: '6px' }}>[{prop.YardiCode}]</span>}
                                                        </h4>
                                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                            {prop.PropertyType && (
                                                                <span className="crm-badge crm-badge--blue">{prop.PropertyType}</span>
                                                            )}
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); (type === 'contact' ? unlinkContactProperty(prop.PropertyId) : type === 'contract' ? unlinkContractProperty(prop.PropertyId) : unlinkProperty(prop.PropertyId)); }}
                                                                className="crm-btn crm-btn--secondary crm-btn--icon"
                                                                style={{ padding: '2px 6px' }}
                                                                data-tooltip="Unlink"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <p style={{ fontSize: '0.813rem', color: 'var(--text-muted)', margin: '0.25rem 0' }}>
                                                        {prop.PropertyAddress}
                                                        {prop.City && `, ${prop.City}`}
                                                        {prop.State && `, ${prop.State}`}
                                                    </p>
                                                    <div className="crm-detail-modal__list-item-meta">
                                                        {prop.Units && <span>Units: {prop.Units}</span>}
                                                        {prop.MarketValue && (
                                                            <span style={{ color: 'var(--success)', fontWeight: '600' }}>
                                                                ${Number(prop.MarketValue).toLocaleString()}
                                                            </span>
                                                        )}
                                                        {type === 'lead' && prop.OwnerName && (
                                                            <span>Owner: {prop.OwnerName}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>)}
                                </div>
                            )}

                            {/* Contracts Tab (Contact only) */}
                            {type === 'contact' && activeDetailTab === 'contracts' && (
                                <div>
                                    {contactLoading ? inlineLoading : (<>
                                    {contracts.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No contracts</p>
                                    ) : (
                                        <div className="crm-detail-modal__list">
                                            {contracts.map(contract => (
                                                <div key={contract.ContractId} className="crm-detail-modal__list-item">
                                                    <div className="crm-detail-modal__list-item-header">
                                                        <h4 className="crm-detail-modal__list-item-title">{contract.ContractNumber}</h4>
                                                        <span className={`crm-badge crm-badge--${contract.Status === 'Active' ? 'green' :
                                                                contract.Status === 'Signed' ? 'blue' :
                                                                    contract.Status === 'Draft' ? 'yellow' : 'blue'
                                                            }`}>
                                                            {contract.Status}
                                                        </span>
                                                    </div>
                                                    <p style={{ fontSize: '0.813rem', color: 'var(--text-secondary)', margin: '0.25rem 0' }}>
                                                        {contract.ContractType}
                                                    </p>
                                                    {contract.PropertyAddress && (
                                                        <p style={{ fontSize: '0.813rem', color: 'var(--text-muted)', margin: '0.25rem 0' }}>
                                                            {contract.PropertyAddress}
                                                        </p>
                                                    )}
                                                    <div className="crm-detail-modal__list-item-meta">
                                                        {contract.Value && (
                                                            <span style={{ color: 'var(--success)', fontWeight: '600' }}>
                                                                {formatMoney(contract.Value)}
                                                            </span>
                                                        )}
                                                        {contract.StartDate && (
                                                            <span>Start: {formatDateOnly(contract.StartDate)}</span>
                                                        )}
                                                        {contract.EndDate && (
                                                            <span>End: {formatDateOnly(contract.EndDate)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>)}
                                </div>
                            )}

                            {/* New Activity Tab (Lead only) */}
                            {activeDetailTab === 'reminders' && (
                                <div>
                                    {contactLoading ? inlineLoading : (<>
                                    {/* Existing Reminders List */}
                                    {itemReminders.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No reminders for this {type}</p>
                                    ) : (
                                        <div className="crm-detail-modal__list">
                                            {itemReminders.map(rem => (
                                                <div key={rem.ReminderId} className="crm-detail-modal__list-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                                            <span className={`crm-badge crm-badge--${rem.Priority === 'High' || rem.Priority === 'Urgent' ? 'red' : rem.Priority === 'Medium' ? 'yellow' : 'blue'}`}>
                                                                {rem.Priority}
                                                            </span>
                                                            <span className="crm-badge crm-badge--purple">{rem.ReminderType}</span>
                                                            {rem.Status === 'Completed' && <span className="crm-badge crm-badge--green">Done</span>}
                                                        </div>
                                                        <p style={{ fontWeight: 500, margin: '0.25rem 0' }}>{rem.Title}</p>
                                                        {rem.Description && <p className="crm-text--muted" style={{ fontSize: '0.813rem', margin: 0 }}>{rem.Description}</p>}
                                                        <div className="crm-detail-modal__note-meta" style={{ marginTop: '0.25rem' }}>
                                                            <span><Clock size={12} style={{ marginRight: '4px' }} />Due: {formatDateOnly(rem.DueDate)}{rem.DueTime ? ` at ${formatTimeOnly(rem.DueTime)}` : ''}</span>
                                                            {parseAssignees(rem).length > 0 && (
                                                                <span style={{ marginLeft: '0.75rem' }}>
                                                                    <User size={12} style={{ marginRight: '4px', verticalAlign: '-2px' }} />
                                                                    {parseAssignees(rem).map(a => a.Name).join(', ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {rem.Status !== 'Completed' && (
                                                        <button onClick={() => completeReminder(rem.ReminderId)} className="crm-btn crm-btn--success" data-tooltip="Complete" style={{ flexShrink: 0, marginLeft: '0.5rem' }}>
                                                            <Check size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>)}
                                </div>
                            )}

                            {activeDetailTab === 'new activity' && (
                                <div>
                                    {/* Add Activity Form */}
                                    <div className="crm-activity__add-form">
                                        <div className="crm-form__row" style={{ alignItems: 'flex-end', flexWrap: 'nowrap' }}>
                                            <div className="crm-form__group crm-form__col" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                                                <select
                                                    value={newActivity.activityType}
                                                    onChange={(e) => setNewActivity(prev => ({ ...prev, activityType: e.target.value }))}
                                                    className="crm-form__select"
                                                >
                                                    {renderOptions(dropdownOptions.ActivityType)}
                                                </select>
                                            </div>
                                            <div className="crm-form__group crm-form__col" style={{ marginBottom: 0 }}>
                                                <input
                                                    type="text"
                                                    placeholder="What happened?"
                                                    value={newActivity.description}
                                                    onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
                                                    className="crm-form__input"
                                                    onKeyDown={(e) => { if (e.key === 'Enter') addActivity(); }}
                                                />
                                            </div>
                                            <button onClick={addActivity} className="crm-btn crm-btn--primary" style={{ flexShrink: 0 }}>
                                                Log
                                            </button>
                                        </div>
                                    </div>

                                    {/* Activity Timeline */}
                                    {activities.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No activities logged yet</p>
                                    ) : (
                                        <div className="crm-activity-timeline">
                                            {activities.map(activity => (
                                                <div key={activity.ActivityId} className="crm-activity-timeline__item">
                                                    <div className="crm-activity-timeline__marker" />
                                                    <div className="crm-activity-timeline__content">
                                                        <div className="crm-activity-timeline__header">
                                                            <span className="crm-badge crm-badge--blue">{activity.ActivityType}</span>
                                                            <span className="crm-activity-timeline__date">
                                                                {new Date(activity.CreatedDate).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        <p className="crm-activity-timeline__description">{activity.Description}</p>
                                                        <div className="crm-activity-timeline__footer">
                                                            {activity.CreatedByName && (
                                                                <span className="crm-activity-timeline__user">
                                                                    <User size={12} /> {activity.CreatedByName}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Documents Tab */}
                            {activeDetailTab === 'documents' && (
                                <div>
                                    {contactLoading ? inlineLoading : (<>
                                    {/* Upload / Link Toggle */}
                                    <div className="crm-documents__actions">
                                        {!showUploadForm ? (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => { setShowUploadForm(true); setUploadType('file'); }} className="crm-btn crm-btn--primary">
                                                    <Plus size={16} />
                                                    Upload File
                                                </button>
                                                <button onClick={() => { setShowUploadForm(true); setUploadType('url'); }} className="crm-btn crm-btn--secondary">
                                                    <Plus size={16} />
                                                    Add Link
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="crm-documents__upload-form">
                                                {uploadType === 'file' ? (
                                                    <div>
                                                        <div
                                                            className={`crm-dropzone ${dragOver ? 'crm-dropzone--active' : ''}`}
                                                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                                            onDragLeave={() => setDragOver(false)}
                                                            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFilesSelected(e.dataTransfer.files); }}
                                                            onClick={() => document.getElementById('doc-file-input').click()}
                                                        >
                                                            <Upload size={24} style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }} />
                                                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                                                                Drop files here or click to browse
                                                            </p>
                                                            <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                                PDF, Word, Excel, PowerPoint, images, CSV — max 25MB each
                                                            </p>
                                                            <input
                                                                id="doc-file-input"
                                                                type="file"
                                                                multiple
                                                                onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }}
                                                                style={{ display: 'none' }}
                                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif"
                                                            />
                                                        </div>
                                                        {pendingFiles.length > 0 && (
                                                            <div style={{ marginTop: '0.75rem' }}>
                                                                {pendingFiles.map((f, i) => (
                                                                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.375rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '6px', marginBottom: '0.375rem', fontSize: '0.813rem' }}>
                                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{f.name}</span>
                                                                        <span style={{ color: 'var(--text-muted)', marginLeft: '0.75rem', flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} KB</span>
                                                                        <button onClick={() => removePendingFile(i)} className="crm-btn crm-btn--secondary crm-btn--icon" style={{ padding: '2px', marginLeft: '0.5rem' }}>
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                {canPublishToInvestorDashboard && pendingFiles.some(f => /\.(xlsx|xls|csv)$/i.test(f.name)) && (
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.813rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isInvestorDashboard}
                                                                            onChange={(e) => setIsInvestorDashboard(e.target.checked)}
                                                                            style={{ accentColor: 'var(--primary)' }}
                                                                        />
                                                                        Use for Investor Dashboard
                                                                    </label>
                                                                )}
                                                                {canPublishToInvestorDashboard && (
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.813rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isInvestorVisible}
                                                                            onChange={(e) => setIsInvestorVisible(e.target.checked)}
                                                                            style={{ accentColor: 'var(--primary)' }}
                                                                        />
                                                                        Visible to investor
                                                                    </label>
                                                                )}
                                                                <button onClick={uploadPendingFiles} className="crm-btn crm-btn--primary" style={{ marginTop: '0.5rem' }} disabled={uploading}>
                                                                    {uploading ? 'Uploading...' : `Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}`}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="crm-form__group">
                                                            <label className="crm-form__label">URL *</label>
                                                            <input
                                                                type="url"
                                                                placeholder="https://..."
                                                                value={linkForm.externalUrl}
                                                                onChange={(e) => setLinkForm(prev => ({ ...prev, externalUrl: e.target.value }))}
                                                                className="crm-form__input"
                                                            />
                                                        </div>
                                                        <div className="crm-form__group">
                                                            <label className="crm-form__label">Display Name</label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. Budget Proposal v2"
                                                                value={linkForm.fileName}
                                                                onChange={(e) => setLinkForm(prev => ({ ...prev, fileName: e.target.value }))}
                                                                className="crm-form__input"
                                                            />
                                                        </div>
                                                        <div className="crm-form__group">
                                                            <label className="crm-form__label">Notes</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Optional notes..."
                                                                value={linkForm.notes}
                                                                onChange={(e) => setLinkForm(prev => ({ ...prev, notes: e.target.value }))}
                                                                className="crm-form__input"
                                                            />
                                                        </div>
                                                        <button onClick={addLink} className="crm-btn crm-btn--primary" disabled={!linkForm.externalUrl.trim()}>
                                                            Save Link
                                                        </button>
                                                    </div>
                                                )}
                                                <button onClick={() => { setShowUploadForm(false); setPendingFiles([]); setIsInvestorDashboard(false); }} className="crm-btn crm-btn--secondary" style={{ marginTop: '0.5rem' }}>
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Document List */}
                                    {documents.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No documents yet</p>
                                    ) : (
                                        <div className="crm-detail-modal__list">
                                            {documents.map(doc => (
                                                <div key={doc.DocumentId} className="crm-detail-modal__list-item">
                                                    <div className="crm-detail-modal__list-item-header">
                                                        <h4 className="crm-detail-modal__list-item-title">
                                                            {doc.ExternalUrl ? (
                                                                <a href={doc.ExternalUrl} target="_blank" rel="noopener noreferrer" className="crm-document__link">
                                                                    {doc.FileName}
                                                                </a>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openDocument(doc)}
                                                                    className="crm-document__link"
                                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                                                                >
                                                                    {doc.FileName}
                                                                </button>
                                                            )}
                                                        </h4>
                                                        <button onClick={() => deleteDocument(doc.DocumentId)} className="crm-btn crm-btn--danger crm-btn--table" data-tooltip="Delete">
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                    <div className="crm-detail-modal__list-item-meta">
                                                        <span>{doc.DocumentType}</span>
                                                        {canPublishToInvestorDashboard && doc.FileName && /\.(xlsx|xls|csv)$/i.test(doc.FileName) && (
                                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600, color: (doc.IsInvestorDashboard === true || doc.IsInvestorDashboard === 1) ? 'var(--primary)' : 'var(--text-muted)' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={doc.IsInvestorDashboard === true || doc.IsInvestorDashboard === 1}
                                                                    onChange={() => toggleDashboardFlag(doc.DocumentId, doc.IsInvestorDashboard === true || doc.IsInvestorDashboard === 1)}
                                                                    style={{ accentColor: 'var(--primary)' }}
                                                                />
                                                                Dashboard
                                                            </label>
                                                        )}
                                                        {canPublishToInvestorDashboard && (
                                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600, color: (doc.IsInvestorVisible === true || doc.IsInvestorVisible === 1) ? 'var(--primary)' : 'var(--text-muted)' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={doc.IsInvestorVisible === true || doc.IsInvestorVisible === 1}
                                                                    onChange={() => toggleInvestorVisibleFlag(doc.DocumentId, doc.IsInvestorVisible === true || doc.IsInvestorVisible === 1)}
                                                                    style={{ accentColor: 'var(--primary)' }}
                                                                />
                                                                Investor
                                                            </label>
                                                        )}
                                                        {doc.FileSize && <span>{(doc.FileSize / 1024).toFixed(0)} KB</span>}
                                                        <span>{new Date(doc.UploadedDate).toLocaleDateString()}</span>
                                                        {doc.UploadedByName && <span>{doc.UploadedByName}</span>}
                                                    </div>
                                                    {doc.Notes && (
                                                        <p style={{ fontSize: '0.813rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>{doc.Notes}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>)}
                                </div>
                            )}

                            {/* Notes Tab */}
                            {activeDetailTab === 'notes' && (
                                <div>
                                    {contactLoading ? inlineLoading : (<>
                                    {/* Add Note */}
                                    <div className="crm-detail-modal__add-note">
                                        <textarea
                                            className="crm-form__textarea"
                                            placeholder="Add a note..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            rows={2}
                                        />
                                        <button onClick={addNote} className="crm-btn crm-btn--primary" disabled={!newNote.trim()}>
                                            Add Note
                                        </button>
                                    </div>

                                    {notes.length === 0 ? (
                                        <p className="crm-text--muted crm-text--center" style={{ padding: '2rem' }}>No notes yet</p>
                                    ) : (
                                        <div className="crm-detail-modal__list">
                                            {notes.map(note => (
                                                <div key={note.NoteId} className="crm-detail-modal__list-item">
                                                    <p className="crm-detail-modal__note-text">{note.NoteText}</p>
                                                    <div className="crm-detail-modal__note-meta">
                                                        <span>{note.CreatedByName || 'System'}</span>
                                                        <span>{new Date(note.CreatedDate).toLocaleDateString()} {new Date(note.CreatedDate).toLocaleTimeString()}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>)}
                                </div>
                            )}

                            </div>{/* End left panel wrapper */}

                            {/* RIGHT PANEL - Activity Timeline (Lead/Contact, always visible) */}
                            {(type === 'lead' || type === 'contact') && (
                                <div className="crm-detail-modal__right-panel">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                        <h3 className="crm-timeline-panel__title" style={{ margin: 0 }}>Activity Timeline</h3>
                                        {type === 'contact' && (
                                            <button onClick={() => fetchEmails(emailMonths)} disabled={emailsLoading || !emailTrackingEnabled || !item.Email} className="crm-btn crm-btn--secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>
                                                {emailsLoading ? 'Loading...' : 'Refresh'}
                                            </button>
                                        )}
                                    </div>
                                    {type === 'contact' && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', marginBottom: '0.75rem', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                            <input
                                                type="checkbox"
                                                checked={emailTrackingEnabled}
                                                onChange={toggleEmailTracking}
                                                disabled={togglingEmailTracking}
                                            />
                                            Track email communications
                                        </label>
                                    )}
                                    {type === 'contact' && emailsError && (
                                        <p style={{ fontSize: '0.75rem', color: 'var(--danger)', margin: '0 0 0.5rem' }}>
                                            Couldn't load emails — your Outlook connection may need reauthorizing.
                                        </p>
                                    )}
                                    {(contactLoading || (type === 'contact' && !emailsLoaded)) ? inlineLoading : (type === 'contact' ? mergedTimeline : activities).length === 0 ? (
                                        <p className="crm-text--muted" style={{ fontSize: '0.813rem', padding: '1rem 0' }}>No activity yet</p>
                                    ) : (
                                        <>
                                        <div className="crm-activity-timeline">
                                            {(type === 'contact' ? mergedTimeline : activities).map(entry => (
                                                entry._source === 'email' ? (
                                                    <div key={`email-${entry.id}`} className="crm-activity-timeline__item">
                                                        <div className="crm-activity-timeline__marker" data-type="email" />
                                                        <div className="crm-activity-timeline__content">
                                                            <div className="crm-activity-timeline__header">
                                                                <span className={`crm-badge ${entry.direction === 'received' ? 'crm-badge--blue' : 'crm-badge--green'}`}>
                                                                    {entry.direction === 'received' ? 'Email Received' : 'Email Sent'}
                                                                </span>
                                                            </div>
                                                            <p className="crm-activity-timeline__description">{entry.subject}</p>
                                                            <div className="crm-activity-timeline__footer">
                                                                <span className="crm-activity-timeline__user">
                                                                    {/* The contact is implied by the page — show the internal
                                                                        Cagan party instead. Sent: the actual sender (fromName);
                                                                        SyncedByUserId is unreliable for sent mail (dedupe means
                                                                        "who synced first", which can be a CC'd colleague).
                                                                        Received: the mailbox owner it was harvested from. */}
                                                                    <Mail size={12} /> {entry.direction === 'received'
                                                                        ? (entry.syncedByName || (entry.to || [])[0]
                                                                            ? `Received by ${entry.syncedByName || (entry.to || [])[0]}`
                                                                            : 'Received')
                                                                        : (entry.fromName || entry.from
                                                                            ? `Sent by ${entry.fromName || entry.from}`
                                                                            : 'Sent')}
                                                                </span>
                                                                <span className="crm-activity-timeline__date">
                                                                    {new Date(entry.receivedDateTime).toLocaleDateString()} {new Date(entry.receivedDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={`activity-${entry.ActivityId}`} className="crm-activity-timeline__item">
                                                        <div className="crm-activity-timeline__marker" data-type={entry.ActivityType === 'Stage Change' ? 'stage' : 'activity'} />
                                                        <div className="crm-activity-timeline__content">
                                                            <div className="crm-activity-timeline__header">
                                                                <span className={`crm-badge ${entry.ActivityType === 'Stage Change' ? 'crm-badge--purple' : 'crm-badge--blue'}`}>
                                                                    {entry.ActivityType}
                                                                </span>
                                                            </div>
                                                            {entry.Description && (
                                                                <p className="crm-activity-timeline__description">{entry.Description}</p>
                                                            )}
                                                            <div className="crm-activity-timeline__footer">
                                                                <span className="crm-activity-timeline__user">
                                                                    User #{entry.CreatedBy || '?'}{entry.CreatedByName ? ` (${entry.CreatedByName})` : ''}
                                                                </span>
                                                                <span className="crm-activity-timeline__date">
                                                                    {new Date(entry.CreatedDate).toLocaleDateString()} {new Date(entry.CreatedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                        {type === 'contact' && emailTrackingEnabled && item.Email && emailMonths > 0 && contactEmails.length > 0 && (
                                            <button
                                                onClick={() => {
                                                    const next = emailMonths === 24 ? 48 : 0;
                                                    setEmailMonths(next);
                                                    fetchEmails(next);
                                                }}
                                                disabled={emailsLoading}
                                                className="crm-btn crm-btn--secondary"
                                                style={{ width: '100%', marginTop: '0.75rem', fontSize: '12px' }}
                                            >
                                                {emailsLoading ? 'Loading...' : emailMonths === 24 ? 'Load older emails (4 years)' : 'Load all emails'}
                                            </button>
                                        )}
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// Convert Lead to Contract Wizard

export default DetailModal;
